import React from 'react';
import { motion } from 'framer-motion';
import { ScrollText, HeartHandshake } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { MEMBERSHIPS, PRO_BONO } from '../mock/data';

const typeStyles = {
  Professional: {
    border: 'rgba(196,163,90,0.35)',
    bg: 'rgba(196,163,90,0.05)',
    color: '#C4A35A',
  },
  Academic: {
    border: 'rgba(240,237,232,0.25)',
    bg: 'rgba(240,237,232,0.03)',
    color: '#F0EDE8',
  },
  Advocacy: {
    border: 'rgba(227,200,132,0.4)',
    bg: 'rgba(227,200,132,0.06)',
    color: '#E3C884',
  },
};

const Organizations = () => {
  const { t } = useLanguage();

  return (
    <section id="organizations" className="section relative" style={{ background: '#0a0f1e' }}>
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <div className="label-small mb-4">{t.orgs.label}</div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#F0EDE8] leading-[1.05]">
            {t.orgs.heading}
          </h2>
          <div className="gold-rule mt-6" />
        </motion.div>

        {/* Memberships */}
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-8">
            <ScrollText size={16} className="text-[#C4A35A]" />
            <h3 className="font-heading text-2xl text-[#F0EDE8]">{t.orgs.memberships}</h3>
            <div className="gold-rule-full flex-1 ml-4" />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-5 mb-8">
            {Object.entries(typeStyles).map(([type, s]) => (
              <div key={type} className="flex items-center gap-2">
                <span className="inline-block w-2 h-2" style={{ background: s.color }} />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#8a7a54]">{t.orgs.types[type]}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {MEMBERSHIPS.map((m, i) => {
              const s = typeStyles[m.type];
              return (
                <motion.div
                  key={m.name}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                  className="relative border p-5 transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    borderColor: s.border,
                    background: s.bg,
                  }}
                >
                  <div
                    className="absolute left-0 top-0 bottom-0 w-[3px]"
                    style={{ background: s.color }}
                  />
                  <div className="text-[9px] tracking-[0.4em] uppercase mb-2" style={{ color: s.color }}>
                    {t.orgs.types[m.type]}
                  </div>
                  <div className="font-heading text-lg text-[#F0EDE8] leading-tight">{m.name}</div>
                  {m.full && (
                    <div className="text-xs text-[#8a7a54] mt-1 leading-snug">{m.full}</div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Pro Bono */}
        <div className="mt-20">
          <div className="flex items-center gap-3 mb-8">
            <HeartHandshake size={16} className="text-[#C4A35A]" />
            <h3 className="font-heading text-2xl text-[#F0EDE8]">{t.orgs.proBono}</h3>
            <div className="gold-rule-full flex-1 ml-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {PRO_BONO.map((p, i) => (
              <motion.div
                key={p}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-center gap-4 border-l border-[#C4A35A] pl-5 py-3"
              >
                <span className="font-heading text-[#C4A35A] text-sm">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-[#DCCFAD] text-sm md:text-[15px]">{p}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Organizations;
